import { motion } from "framer-motion";
import { Activity, BellRing, LineChart, ScrollText } from "lucide-react";

const KPIS = [
  ["Ready to Ship", "128", "text-blue-600"],
  ["Inbound Waiting", "34", "text-indigo-600"],
  ["Returns Pending", "12", "text-amber-600"],
  ["Low Stock SKUs", "7", "text-red-600"],
];

const PANELS = [
  { icon: LineChart, title: "Trend Charts", body: "Shipped-per-day trends across every store, live from operational data." },
  { icon: Activity, title: "Activity Log", body: "Every create, update and delete with a previous → new value diff. Fully searchable." },
  { icon: BellRing, title: "Notifications", body: "Store-scoped alerts: low stock, quantity problems, inbound arrivals." },
  { icon: ScrollText, title: "CSV Everywhere", body: "Any table, any filter — exported for finance, clients or audits." },
];

export const ControlRoomSection = () => (
  <section id="command" className="relative py-24 lg:py-32 lp-grid-lines" data-testid="command-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-blue-600 font-bold mb-4">Command Center</p>
      <h2 className="lp-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0b1530] max-w-2xl">
        A control room, not a spreadsheet.
      </h2>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="mt-14 rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-2xl shadow-blue-600/10"
        data-testid="command-dashboard-mock"
      >
        <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-white">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ml-4 text-xs md:text-sm text-slate-500 tracking-wide">fulfillflow — live dashboard</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {KPIS.map(([label, value, color]) => (
            <div key={label} className="bg-white p-7 lg:p-9">
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-slate-500">{label}</p>
              <p className={`lp-display text-4xl lg:text-5xl font-semibold mt-3 ${color}`}>{value}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border-t border-slate-200">
          {PANELS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white p-7 lg:p-8 hover:bg-blue-50/50 transition-colors duration-300"
              data-testid={`command-panel-${i}`}
            >
              <p.icon className="text-blue-600 mb-4" size={23} strokeWidth={1.5} />
              <h3 className="text-base font-semibold text-[#0b1530]">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);
